const yaml = require("js-yaml");

export class UserStory {
  constructor(title, detail, tags) {
    this.title = title;
    this.detail = detail;
    this.tags = tags;
  }
}

export class UserStoryGroup {
  static types = { backbone: "backbone", slice: "slice" };

  constructor(userStory, groupName, groupItems, type) {
    const validTypes = Object.values(UserStoryGroup.types);
    if (!validTypes.includes(type)) {
      throw new Error(
        `Invalid type: ${type}. Expected one of ${validTypes.join(", ")}`
      );
    }
    this.userStory = userStory;
    this.groupName = groupName;
    this.groupItems = groupItems;
    this.type = type;
  }

  isBackbone() {
    return this.type == UserStoryGroup.types.backbone;
  }

  isSlice() {
    return this.type == UserStoryGroup.types.slice;
  }

  hasBackbone() {
    return this.groupItems.some((item) => item.isBackbone());
  }

  hasSlice() {
    return this.groupItems.some((item) => item.isSlice());
  }

  add(item) {
    if (this.type === UserStoryGroup.types.backbone && item instanceof UserStoryGroup) {
      this.groupItems.push(item);
    } else if (this.type === UserStoryGroup.types.slice && item instanceof UserStory) {
      this.groupItems.push(item);
    } else {
      throw new Error(
        `Invalid item type. Expected 'UserStory' for 'slice' type or 'UserStoryGroup' for 'backbone' type.`
      );
    }
  }
}

export class Settings {
  constructor(settings) {
    this.settings = Object.assign({}, Settings.initialSettings(), settings);
  }

  backboneNames() {
    return this.settings.groups.backbones.map((backbone) => backbone.name);
  }

  sliceNames() {
    return this.settings.groups.slices.map((slice) => slice.name);
  }

  getGroupSettings(userStoryGroup) {
    const merged = Object.assign({}, Settings.initialSettings(), this.settings);
    const group = userStoryGroup.isBackbone()
      ? this.settings.groups.backbones
      : this.settings.groups.slices;
    const groupSettings = group.find((e) => e.name == userStoryGroup.groupName);
    return Object.assign({}, merged.note.styles, groupSettings);
  }

  getEffective(userStoryGroup) {}

  getGroupLabel(userStoryGroup) {
    const groups = this.settings.groups;
    const settings = userStoryGroup.isBackbone()
      ? groups.backbones
      : groups.slices;
    const setting = settings.filter(
      (item) => item.name == userStoryGroup.groupName
    );
    return setting[0] ? setting[0].label : "";
  }

  static verify(settings) {
    const note = settings.note;
    const groups = settings.groups;
    if (settings.length > 2) return false;
    if (!(settings.note && settings.groups)) return false;
    return true;
  }

  static initialSettings() {
    return {
      note: {
        styles: {
          width: "100px",
          height: "75px",
          bgcolor: "lightyellow",
        },
      },
      groups: {
        backbones: [
          {
            name: "epics",
            label: "EPICS",
            bgcolor: "pink",
          },
          {
            name: "acts",
            label: "ACTIVITIES",
            bgcolor: "skyblue",
          },
        ],
        slices: [
          { name: "release1", label: "RELEASE 1", visible: true },
          { name: "release2", label: "RELEASE 2", visible: true },
          { name: "release3", label: "RELEASE 3", visible: true },
          { name: "release4", label: "RELEASE 4", visible: true },
          { name: "release5", label: "RELEASE 5", visible: true },
          { name: "release6", label: "RELEASE 6", visible: true },
          { name: "release7", label: "RELEASE 7", visible: true },
          { name: "release8", label: "RELEASE 8", visible: true },
          { name: "release9", label: "RELEASE 9", visible: true },
          { name: "release10", label: "RELEASE 10", visible: true },
          { name: "release11", label: "RELEASE 11", visible: true },
          { name: "release12", label: "RELEASE 12", visible: true },
          { name: "release13", label: "RELEASE 13", visible: true },
          { name: "release14", label: "RELEASE 14", visible: true },
          { name: "release15", label: "RELEASE 15", visible: true },
          { name: "release16", label: "RELEASE 16", visible: true },
          { name: "release17", label: "RELEASE 17", visible: true },
          { name: "release18", label: "RELEASE 18", visible: true },
          { name: "release19", label: "RELEASE 19", visible: true },
          { name: "release20", label: "RELEASE 20", visible: true },
        ],
      },
    };
  }
}

export class UserStoryYaml {
  static loadYaml(yamlString) {
    try {
      return { data: yaml.load(yamlString), isError: false };
    } catch (e) {
      return { data: null, isError: true };
    }
  }

  static verify(data) {
    if (!data) return false;
    if (data.length > 2) return false;
    if (!(data.settings && data.data)) return false;
    return true;
  }

  static fromYaml(yamlString) {
    const defaultOutput = { data: null, settings: null, isError: true };

    try {
      const { data, isError } = this.loadYaml(yamlString);
      if (isError) return defaultOutput;
      const settings = new Settings(data.settings);
      const parsedData = data.data
        ? this.parseRawData(
            data.data,
            settings.backboneNames(),
            settings.sliceNames()
          )
        : [];
      return { settings: settings, data: parsedData, isError: false };
    } catch (e) {
      return defaultOutput;
    }
  }

  static parseRawData(data, backboneNames, sliceNames) {
    const result = [];
    function parseNode(node, level = 0, groupName) {
      if (Array.isArray(node)) {
        return node.map((n) => parseNode(n, level, groupName));
      }
      if (node == null) {
        return null;
      }
      if (typeof node === "object") {
        const userStory = new UserStory(node[":"], node.detail, node.tags);
        const groupItems = [];
        for (const key of Object.keys(node)) {
          if (backboneNames.includes(key)) {
            groupItems.push(...parseNode(node[key], level + 1, key));
          }
          if (sliceNames.includes(key)) {
            const parsed = parseNode(node[key], level + 1, key);
            groupItems.push(
              ...[
                new UserStoryGroup(
                  null,
                  key,
                  parsed,
                  UserStoryGroup.types.slice
                ),
              ]
            );
          }
        }
        return sliceNames.includes(groupName)
          ? userStory
          : new UserStoryGroup(
              userStory,
              groupName,
              groupItems,
              UserStoryGroup.types.backbone
            );
      }
      return null;
    }
    for (const key of Object.keys(data)) {
      if (backboneNames.includes(key)) {
        result.push(...parseNode(data[key], 0, key));
      }
    }
    return result;
  }
}

export class UserStoryGroupCollection {
  constructor(userStoryGroups, settings) {
    this.data = userStoryGroups;
    this.settings = settings || Settings.initialSettings();
    this.backboneNames = this.settings.backboneNames();
    this.sliceNames = this.settings.sliceNames();
    this.adjustSlices();
    this.equalizeSliceItems(1);
  }

  getMaxSliceItems() {
    const sliceCounts = this.sliceNames.map((name) => ({ name, count: 0 }));

    const countItemsRecursively = (group) => {
      if (
        group instanceof UserStoryGroup &&
        this.sliceNames.includes(group.groupName)
      ) {
        const sliceIndex = sliceCounts.findIndex(
          (slice) => slice.name === group.groupName
        );
        sliceCounts[sliceIndex].count = Math.max(
          sliceCounts[sliceIndex].count,
          group.groupItems.length
        );
      }
      group.groupItems.forEach((item) => {
        if (item instanceof UserStoryGroup) {
          countItemsRecursively(item);
        }
      });
    };

    this.data.forEach((group) => {
      if (this.backboneNames.includes(group.groupName)) {
        countItemsRecursively(group);
      }
    });

    return sliceCounts;
  }

  equalizeSliceItems(minItems) {
    // Step 1: Calculate the maximum number of items for each slice
    const maxSliceItems = this.getMaxSliceItems().map(e => {return {name: e.name, count: Math.max(minItems, e.count)}});
    const maxCounts = maxSliceItems.map((item) => item.count);

    // Step 2: Adjust the number of items in each slice to match the maximum count for each slice
    this.data.forEach((group) => {
      this.adjustGroupItems(group, maxSliceItems, maxCounts);
    });
  }

  adjustGroupItems(group, maxSliceItems, maxCounts) {
    if (
      group instanceof UserStoryGroup &&
      maxSliceItems.some((item) => item.name === group.groupName)
    ) {
      const currentCount = group.groupItems.length;
      const maxCount =
        maxCounts[
          maxSliceItems.findIndex((item) => item.name === group.groupName)
        ];
      if (currentCount < maxCount) {
        // Add dummy items to reach the maximum count for this slice
        const dummyItems = new Array(maxCount - currentCount).fill(null);
        group.groupItems.push(...dummyItems);
      } else if (currentCount > maxCount) {
        // Remove excessºººººº items to match the maximum count for this slice
        group.groupItems.length = maxCount;
      }
    }

    // Recursively process groupItems
    group.groupItems.forEach((item) => {
      if (item instanceof UserStoryGroup) {
        this.adjustGroupItems(item, maxSliceItems, maxCounts);
      }
    });
  }

  adjustSlices() {
    this.data.forEach((group) => {
      this.adjustSlicesRecursively(group);
    });
  }

  adjustSlicesRecursively(group) {
    if (group instanceof UserStoryGroup) {
      if (group.type == UserStoryGroup.types.backbone) {
        const hasSlice = group.groupItems.some((item) => item.isSlice());
        if (hasSlice) {
          const slicesToAdd = this.sliceNames.filter(
            (name) => !group.groupItems.some((item) => item.groupName === name)
          );
          slicesToAdd.forEach((name) => {
            group.groupItems.push(
              new UserStoryGroup(null, name, [], UserStoryGroup.types.slice)
            );
          });
        }

        group.groupItems.sort((a, b) => {
          const aIndex = this.sliceNames.indexOf(a.groupName);
          const bIndex = this.sliceNames.indexOf(b.groupName);
          return aIndex - bIndex;
        });

        group.groupItems.forEach((subGroup) => {
          this.adjustSlicesRecursively(subGroup);
        });
      }
    }
  }
}
